import { apiSlice } from "../../api/apiSlice";

const joinIdParam = (...values) =>
  [
    ...new Set(
      values
        .flatMap((value) =>
          Array.isArray(value)
            ? value
            : value == null || value === ""
              ? []
              : String(value).split(",")
        )
        .map((id) => String(id).trim())
        .filter(Boolean)
    ),
  ].join(",");

export const promotionsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPromotions: builder.query({
      query: ({
        bookId,
        companyId,
        companyIds,
        status,
        search,
        startDate,
        endDate,
        view,
        page = 1,
        limit = 100,
      }) => {
        const params = new URLSearchParams();
        if (bookId) params.set("bookId", bookId);
        const ids = [
          ...((companyIds || []).map(String).filter(Boolean)),
          ...(companyId ? [String(companyId)] : []),
        ];
        if (ids.length) params.set("companyId", [...new Set(ids)].join(","));
        if (status) params.set("status", status);
        if (search) params.set("search", search);
        if (startDate) params.set("startDate", startDate);
        if (endDate) params.set("endDate", endDate);
        if (view) params.set("view", view);
        params.set("page", String(page));
        params.set("limit", String(limit));
        return {
          url: `/api/promotions?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["Promotions"],
    }),
    getPromotionHistory: builder.query({
      query: ({
        bookId,
        companyId,
        companyIds,
        search,
        startDate,
        endDate,
        page = 1,
        limit = 100,
      }) => {
        const params = new URLSearchParams();
        if (bookId) params.set("bookId", bookId);
        const ids = [
          ...((companyIds || []).map(String).filter(Boolean)),
          ...(companyId ? [String(companyId)] : []),
        ];
        if (ids.length) params.set("companyId", [...new Set(ids)].join(","));
        if (search) params.set("search", search);
        if (startDate) params.set("startDate", startDate);
        if (endDate) params.set("endDate", endDate);
        params.set("page", String(page));
        params.set("limit", String(limit));
        return {
          url: `/api/promotions/history?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["Promotions"],
    }),
    getPromotionInvites: builder.query({
      query: ({
        status,
        bookId,
        bookIds,
        companyId,
        companyIds,
        search,
        createdStartDate,
        createdEndDate,
        eventStartDate,
        eventEndDate,
        page = 1,
        limit = 100,
      }) => {
        const params = new URLSearchParams();
        if (status) params.set("status", status);
        const bookParam = joinIdParam(bookIds, bookId);
        const companyParam = joinIdParam(companyIds, companyId);
        if (bookParam) params.set("bookId", bookParam);
        if (companyParam) params.set("companyId", companyParam);
        if (search) params.set("search", search);
        if (createdStartDate) params.set("createdStartDate", createdStartDate);
        if (createdEndDate) params.set("createdEndDate", createdEndDate);
        if (eventStartDate) params.set("eventStartDate", eventStartDate);
        if (eventEndDate) params.set("eventEndDate", eventEndDate);
        params.set("page", String(page));
        params.set("limit", String(limit));
        return {
          url: `/api/promotions/invites?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["PromotionInvites"],
    }),
    getPromotionInviteById: builder.query({
      query: ({ inviteId, search, companyId }) => {
        const params = new URLSearchParams();
        if (search) params.set("search", search);
        if (companyId) params.set("companyId", companyId);
        const qs = params.toString();
        return {
          url: `/api/promotions/invites/${inviteId}${qs ? `?${qs}` : ""}`,
          method: "GET",
        };
      },
      providesTags: (_result, _err, arg) => [
        { type: "PromotionInvites", id: arg.inviteId },
      ],
    }),
    getInviteEligibleEmployees: builder.query({
      query: ({ inviteId, search, companyIds }) => {
        const params = new URLSearchParams();
        if (search) params.set("search", search);
        const ids = (companyIds || []).map(String).filter(Boolean);
        if (ids.length) params.set("companyId", ids.join(","));
        const qs = params.toString();
        return {
          url: `/api/promotions/invites/${inviteId}/eligible-employees${
            qs ? `?${qs}` : ""
          }`,
          method: "GET",
        };
      },
      serializeQueryArgs: ({ queryArgs }) =>
        [
          queryArgs.inviteId,
          queryArgs.search || "",
          (queryArgs.companyIds || []).map(String).sort().join(","),
        ].join("|"),
      providesTags: (_result, _err, arg) => [
        { type: "PromotionInvites", id: `${arg.inviteId}-eligible` },
      ],
    }),
    getPromotionStatuses: builder.query({
      query: () => ({
        url: "/api/promotions/statuses",
        method: "GET",
      }),
    }),
    createPromotionInvite: builder.mutation({
      query: (body) => ({
        url: "/api/promotions/invites",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Promotions", "PromotionInvites"],
    }),
    syncInviteEmployees: builder.mutation({
      query: ({ inviteId, promotionIds, mode }) => {
        if (mode !== "add" && mode !== "remove" && mode !== "replace") {
          throw new Error(
            "syncInviteEmployees requires mode: add, remove, or replace"
          );
        }
        return {
          url: `/api/promotions/invites/${inviteId}/employees`,
          method: "PUT",
          body: { promotionIds, mode },
        };
      },
      invalidatesTags: (_result, _err, arg) => [
        "Promotions",
        "PromotionInvites",
        { type: "PromotionInvites", id: arg.inviteId },
        { type: "PromotionInvites", id: `${arg.inviteId}-eligible` },
      ],
    }),
    sendPromotionInvite: builder.mutation({
      query: ({ inviteId }) => ({
        url: `/api/promotions/invites/${inviteId}/send`,
        method: "POST",
      }),
      invalidatesTags: (_result, _err, arg) => [
        "Promotions",
        "PromotionInvites",
        { type: "PromotionInvites", id: arg.inviteId },
        { type: "PromotionInvites", id: `${arg.inviteId}-eligible` },
      ],
    }),
    updatePromotionInvite: builder.mutation({
      query: ({ inviteId, ...body }) => ({
        url: `/api/promotions/invites/${inviteId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _err, arg) => [
        "Promotions",
        "PromotionInvites",
        { type: "PromotionInvites", id: arg.inviteId },
      ],
    }),
    cancelPromotionInvite: builder.mutation({
      query: ({ inviteId }) => ({
        url: `/api/promotions/invites/${inviteId}/cancel`,
        method: "POST",
      }),
      invalidatesTags: ["Promotions", "PromotionInvites"],
    }),
    completePromotionInvite: builder.mutation({
      query: ({ inviteId }) => ({
        url: `/api/promotions/invites/${inviteId}/complete`,
        method: "POST",
      }),
      invalidatesTags: (_result, _err, arg) => [
        "Promotions",
        "PromotionInvites",
        { type: "PromotionInvites", id: arg.inviteId },
      ],
    }),
    backdatePromotionInviteEvent: builder.mutation({
      query: ({ inviteId }) => ({
        url: `/api/promotions/invites/${inviteId}/dev/backdate-event`,
        method: "POST",
      }),
      invalidatesTags: (_result, _err, arg) => [
        "Promotions",
        "PromotionInvites",
        { type: "PromotionInvites", id: arg.inviteId },
      ],
    }),
    deletePromotionInvite: builder.mutation({
      query: ({ inviteId }) => ({
        url: `/api/promotions/invites/${inviteId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Promotions", "PromotionInvites"],
    }),
    completeContactPendingPromotions: builder.mutation({
      query: ({ promotionIds }) => ({
        url: "/api/promotions/complete-pending",
        method: "POST",
        body: { promotionIds },
      }),
      invalidatesTags: ["Promotions", "PromotionInvites"],
    }),
    getMyPromotionInvite: builder.query({
      query: (promotionId) => ({
        url: `/api/promotions/${promotionId}/my-invite`,
        method: "GET",
      }),
      providesTags: (_result, _err, promotionId) => [
        "Promotions",
        { type: "Promotions", id: promotionId },
      ],
    }),
    respondToPromotionInvite: builder.mutation({
      query: ({ promotionId, response, reason }) => ({
        url: `/api/promotions/${promotionId}/respond`,
        method: "POST",
        body: { response, reason },
      }),
      invalidatesTags: (_result, _err, arg) => [
        "Promotions",
        "PromotionInvites",
        { type: "Promotions", id: arg.promotionId },
      ],
    }),
    updatePromotionEmployeeStatus: builder.mutation({
      query: ({ promotionId, status, reason }) => ({
        url: `/api/promotions/${promotionId}/status`,
        method: "PATCH",
        body: { status, reason },
      }),
      invalidatesTags: ["Promotions", "PromotionInvites"],
    }),
    remindPromotionInvite: builder.mutation({
      query: ({ promotionId }) => ({
        url: `/api/promotions/${promotionId}/remind`,
        method: "POST",
      }),
      invalidatesTags: ["Promotions", "PromotionInvites"],
    }),
    getPromotionEmailTemplates: builder.query({
      query: () => ({
        url: "/api/promotions/email-templates",
        method: "GET",
      }),
      providesTags: ["PromotionEmailTemplates"],
    }),
    getPromotionEmailTemplate: builder.query({
      query: (templateId) => ({
        url: `/api/promotions/email-templates/${templateId}`,
        method: "GET",
      }),
      providesTags: (_result, _err, templateId) => [
        { type: "PromotionEmailTemplates", id: templateId },
      ],
    }),
    updatePromotionEmailTemplate: builder.mutation({
      query: ({ templateId, ...body }) => ({
        url: `/api/promotions/email-templates/${templateId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _err, arg) => [
        "PromotionEmailTemplates",
        { type: "PromotionEmailTemplates", id: arg.templateId },
      ],
    }),
    resetPromotionEmailTemplate: builder.mutation({
      query: ({ templateId }) => ({
        url: `/api/promotions/email-templates/${templateId}/reset`,
        method: "POST",
      }),
      invalidatesTags: (_result, _err, arg) => [
        "PromotionEmailTemplates",
        { type: "PromotionEmailTemplates", id: arg.templateId },
      ],
    }),
    previewPromotionEmailTemplate: builder.mutation({
      query: ({ templateId, data }) => ({
        url: `/api/promotions/email-templates/${templateId}/preview`,
        method: "POST",
        body: { data },
      }),
    }),
    previewPromotionInviteEmail: builder.mutation({
      query: ({ inviteId }) => ({
        url: `/api/promotions/invites/${inviteId}/preview`,
        method: "POST",
      }),
    }),
    getPromotionEmailSettings: builder.query({
      query: () => ({
        url: "/api/promotions/email-templates/settings",
        method: "GET",
      }),
      providesTags: ["PromotionEmailSettings"],
    }),
    updatePromotionEmailSettings: builder.mutation({
      query: (body) => ({
        url: "/api/promotions/email-templates/settings",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["PromotionEmailSettings", "PromotionEmailTemplates"],
    }),
    resetPromotionEmailSettings: builder.mutation({
      query: (body) => ({
        url: "/api/promotions/email-templates/settings/reset",
        method: "POST",
        body,
      }),
      invalidatesTags: ["PromotionEmailSettings", "PromotionEmailTemplates"],
    }),
    uploadPromotionEmailAsset: builder.mutation({
      query: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: "/api/upload/file",
          method: "POST",
          body: formData,
        };
      },
    }),
  }),
});

export const {
  useGetPromotionsQuery,
  useGetPromotionHistoryQuery,
  useGetPromotionInvitesQuery,
  useGetPromotionInviteByIdQuery,
  useGetInviteEligibleEmployeesQuery,
  useGetPromotionStatusesQuery,
  useCreatePromotionInviteMutation,
  useSyncInviteEmployeesMutation,
  useSendPromotionInviteMutation,
  useUpdatePromotionInviteMutation,
  useCancelPromotionInviteMutation,
  useCompletePromotionInviteMutation,
  useBackdatePromotionInviteEventMutation,
  useDeletePromotionInviteMutation,
  useCompleteContactPendingPromotionsMutation,
  useGetMyPromotionInviteQuery,
  useRespondToPromotionInviteMutation,
  useUpdatePromotionEmployeeStatusMutation,
  useRemindPromotionInviteMutation,
  useGetPromotionEmailTemplatesQuery,
  useGetPromotionEmailTemplateQuery,
  useUpdatePromotionEmailTemplateMutation,
  useResetPromotionEmailTemplateMutation,
  usePreviewPromotionEmailTemplateMutation,
  usePreviewPromotionInviteEmailMutation,
  useGetPromotionEmailSettingsQuery,
  useUpdatePromotionEmailSettingsMutation,
  useResetPromotionEmailSettingsMutation,
  useUploadPromotionEmailAssetMutation,
  useLazyGetPromotionsQuery,
  useLazyGetPromotionInvitesQuery,
} = promotionsApiSlice;
